import apiService from '../api';
import type {
  BlotterReportRequest,
  BlotterReportResponse,
  UserReportRequest,
  UserReportResponse,
  ClientReportRequest,
  ClientReportResponse,
  AccountReportRequest,
  AccountReportResponse,
  CheckReportRequest,
  CheckReportResponse
} from '../../types/report';

class ReportService {
  /**
   * Generate Blotter Report
   */
  async generateBlotterReport(data: BlotterReportRequest): Promise<BlotterReportResponse> {
    const response = await (apiService as any).client.post('/reports/blotter', data);
    return response.data;
  }

  /**
   * Generate User Commission Report
   */
  async generateUserReport(data: UserReportRequest): Promise<UserReportResponse> {
    const response = await (apiService as any).client.post('/reports/user', data);
    return response.data;
  }

  /**
   * Generate Client Commission Report
   */
  async generateClientReport(data: ClientReportRequest): Promise<ClientReportResponse> {
    const response = await (apiService as any).client.post('/reports/client', data);
    return response.data;
  }

  /**
   * Generate Account Commission Report
   */
  async generateAccountReport(data: AccountReportRequest): Promise<AccountReportResponse> {
    const response = await (apiService as any).client.post('/reports/account', data);
    return response.data;
  }

  /**
   * Download Blotter Report as PDF
   */
  async downloadBlotterPdf(data: BlotterReportRequest): Promise<void> {
    try {
      const response = await (apiService as any).client.post('/reports/blotter',
        { ...data, format: 'pdf', auto_print: false },
        { responseType: 'blob' }
      );
      this.downloadFile(response.data, `blotter-report-${data.date}.pdf`);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Print Blotter Report as PDF
   */
  async printBlotterPdf(data: BlotterReportRequest): Promise<void> {
    try {
      const response = await (apiService as any).client.post('/reports/blotter',
        { ...data, format: 'pdf', auto_print: true },
        { responseType: 'blob' }
      );
      this.printFile(response.data);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Download User Report as PDF
   */
  async downloadUserPdf(data: UserReportRequest): Promise<void> {
    try {
      const userLabel = data.user_id ? `user-${data.user_id}` : 'all-users';
      const response = await (apiService as any).client.post('/reports/user',
        { ...data, format: 'pdf', auto_print: false },
        { responseType: 'blob' }
      );
      this.downloadFile(response.data, `user-commission-report-${userLabel}-${data.start_date}-${data.end_date}.pdf`);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Print User Report as PDF
   */
  async printUserPdf(data: UserReportRequest): Promise<void> {
    try {
      const response = await (apiService as any).client.post('/reports/user',
        { ...data, format: 'pdf', auto_print: true },
        { responseType: 'blob' }
      );
      this.printFile(response.data);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Download Client Report as PDF
   */
  async downloadClientPdf(data: ClientReportRequest): Promise<void> {
    try {
      const clientLabel = data.client_id ? `client-${data.client_id}` : 'all-clients';
      const response = await (apiService as any).client.post('/reports/client',
        { ...data, format: 'pdf', auto_print: false },
        { responseType: 'blob' }
      );
      this.downloadFile(response.data, `client-commission-report-${clientLabel}-${data.start_date}-${data.end_date}.pdf`);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Print Client Report as PDF
   */
  async printClientPdf(data: ClientReportRequest): Promise<void> {
    try {
      const response = await (apiService as any).client.post('/reports/client',
        { ...data, format: 'pdf', auto_print: true },
        { responseType: 'blob' }
      );
      this.printFile(response.data);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Download Account Report as PDF
   */
  async downloadAccountPdf(data: AccountReportRequest): Promise<void> {
    try {
      const accountLabel = data.account_id ? `account-${data.account_id}` : 'all-accounts';
      const response = await (apiService as any).client.post('/reports/account',
        { ...data, format: 'pdf', auto_print: false },
        { responseType: 'blob' }
      );
      this.downloadFile(response.data, `account-commission-report-${accountLabel}-${data.start_date}-${data.end_date}.pdf`);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Print Account Report as PDF
   */
  async printAccountPdf(data: AccountReportRequest): Promise<void> {
    try {
      const response = await (apiService as any).client.post('/reports/account',
        { ...data, format: 'pdf', auto_print: true },
        { responseType: 'blob' }
      );
      this.printFile(response.data);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  // -----------------------------------------------------------------
  // Excel (.xlsx) downloads — mirrors the legacy Yii transaction/download
  // -----------------------------------------------------------------

  /**
   * Download Blotter Report as Excel (.xlsx)
   */
  async downloadBlotterExcel(data: BlotterReportRequest): Promise<void> {
    try {
      const response = await (apiService as any).client.post('/reports/blotter',
        { ...data, format: 'xlsx' },
        { responseType: 'blob' }
      );
      this.downloadFile(response.data, `mch-blotter-commission-report_${data.date}.xlsx`);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Download User Report as Excel (.xlsx)
   */
  async downloadUserExcel(data: UserReportRequest): Promise<void> {
    try {
      const userLabel = data.user_id ? `_user-${data.user_id}` : '';
      const response = await (apiService as any).client.post('/reports/user',
        { ...data, format: 'xlsx' },
        { responseType: 'blob' }
      );
      this.downloadFile(response.data, `mch-user-commission-report${userLabel}_${data.start_date}-${data.end_date}.xlsx`);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Download Client Report as Excel (.xlsx)
   */
  async downloadClientExcel(data: ClientReportRequest): Promise<void> {
    try {
      const clientLabel = data.client_id ? `_client-${data.client_id}` : '';
      const response = await (apiService as any).client.post('/reports/client',
        { ...data, format: 'xlsx' },
        { responseType: 'blob' }
      );
      this.downloadFile(response.data, `mch-client-commission-report${clientLabel}_${data.start_date}-${data.end_date}.xlsx`);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Download Account Report as Excel (.xlsx)
   */
  async downloadAccountExcel(data: AccountReportRequest): Promise<void> {
    try {
      const accountLabel = data.account_id ? `_account-${data.account_id}` : '';
      const response = await (apiService as any).client.post('/reports/account',
        { ...data, format: 'xlsx' },
        { responseType: 'blob' }
      );
      this.downloadFile(response.data, `mch-account-commission-report${accountLabel}_${data.start_date}-${data.end_date}.xlsx`);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  // -----------------------------------------------------------------
  // R17 Task H: Check Report by Firm
  // -----------------------------------------------------------------

  /**
   * Generate Check Report by Firm (JSON)
   */
  async generateCheckReport(data: CheckReportRequest): Promise<CheckReportResponse> {
    const response = await (apiService as any).client.post('/reports/checks', data);
    return response.data;
  }

  /**
   * Download Check Report as PDF
   */
  async downloadCheckReportPdf(data: CheckReportRequest): Promise<void> {
    try {
      const response = await (apiService as any).client.post('/reports/checks',
        { ...data, format: 'pdf', auto_print: false },
        { responseType: 'blob' }
      );
      this.downloadFile(response.data, `check-commission-report-${data.start_date}-${data.end_date}.pdf`);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Print Check Report PDF (opens in a new tab)
   */
  async printCheckReportPdf(data: CheckReportRequest): Promise<void> {
    try {
      const response = await (apiService as any).client.post('/reports/checks',
        { ...data, format: 'pdf', auto_print: true },
        { responseType: 'blob' }
      );
      this.printFile(response.data);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Download Check Report as Excel (.xlsx)
   */
  async downloadCheckReportExcel(data: CheckReportRequest): Promise<void> {
    try {
      const response = await (apiService as any).client.post('/reports/checks',
        { ...data, format: 'xlsx' },
        { responseType: 'blob' }
      );
      this.downloadFile(response.data, `mch-check-commission-report_${data.start_date}-${data.end_date}.xlsx`);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Print Employee Blotter Report PDF (detailed - all transactions)
   */
  async printEmployeeBlotter(data: { date: string; user_id: number }): Promise<void> {
    try {
      const response = await (apiService as any).client.post('/reports/blotter/employee-pdf',
        { ...data, auto_print: true },
        { responseType: 'blob' }
      );
      this.printFile(response.data);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Print Consolidated Employee Blotter Report PDF (grouped by client/symbol)
   */
  async printEmployeeBlotterConsolidated(data: { date: string; user_id: number }): Promise<void> {
    try {
      const response = await (apiService as any).client.post('/reports/blotter/employee-pdf-consolidated',
        { ...data, auto_print: true },
        { responseType: 'blob' }
      );
      this.printFile(response.data);
    } catch (error: any) {
      throw new Error(await this.parseBlobError(error));
    }
  }

  /**
   * Parse error from a blob response (Axios delivers error bodies as Blob when responseType is 'blob')
   */
  private async parseBlobError(error: any): Promise<string> {
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        return json.message || json.error || 'Request failed';
      } catch {
        return 'Request failed';
      }
    }
    return error.response?.data?.message || error.message || 'Request failed';
  }

  /**
   * Helper method to trigger file download
   */
  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Helper to open a PDF blob in a new tab so the user can use the
   * browser's built-in PDF viewer + print button.
   *
   * Why NOT auto-invoke print() any more?
   *   The prior iframe + contentWindow.print() approach suffered from
   *   several cross-browser failure modes:
   *     - iframe.onload firing multiple times on Chrome/Edge PDF viewers
   *       (double print dialog), or not firing at all on Firefox pdf.js.
   *     - contentWindow.print() silently no-oping on partially-loaded PDF
   *       viewers, which then suppressed the 5s popup fallback.
   *     - The 5s fallback opening a *second* tab because the catch branch
   *       also opened one — duplicate tabs on flaky loads.
   *     - The 5s fallback firing *outside* the user gesture window on
   *       slow networks — popup blockers silently killing it.
   *     - The 120s cleanup revoking the blob URL out from under the
   *       fallback tab that was still using it.
   *
   *   Opening the blob URL in a new tab from inside the click handler is
   *   bulletproof across Chrome, Edge, Firefox, and Safari: the tab
   *   inherits the user gesture (no popup block), the native PDF viewer
   *   renders the file, and its toolbar exposes the standard Print
   *   control. Peter has to click Print in the viewer, but the flow
   *   never silently fails — which is what CRIT #1 was about.
   *
   * Blob URL lifetime: we delay revocation so the new tab has time to
   * read from the URL. 5 minutes is enough for a user to print and
   * still generous vs. the previous 120s.
   */
  private printFile(blob: Blob): void {
    const url = window.URL.createObjectURL(blob);

    // Use a hidden anchor with target=_blank instead of window.open(). Two reasons:
    //   1. window.open(url, '_blank', 'noopener,noreferrer') returns null in
    //      several browsers by design — 'noopener' explicitly severs the
    //      returned window reference EVEN WHEN the tab opens successfully.
    //      That produced a false "Failed to print" toast in R14.1 while the
    //      PDF loaded fine — Peter saw it and reported it.
    //   2. Anchor-click inherits the user gesture reliably (same as window.open
    //      inside a click handler), and the browser handles blob: same-origin
    //      security correctly without needing the noopener hint.
    // rel='noopener noreferrer' on the anchor still gives us the security
    // hardening that noopener would have provided — without the null-return
    // gotcha.
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Revoke the blob URL after 5 minutes — enough for the user to print
    // and then some, without leaking memory across long sessions.
    window.setTimeout(() => window.URL.revokeObjectURL(url), 5 * 60 * 1000);
  }
}

export default new ReportService();
